import { Box, Flex } from '@rebass/grid';
import fileDownload from 'js-file-download';
import * as React from 'react';
import Helmet from 'react-helmet';

import { Account, Search, Sponsor } from '../../api';
import {
  HACKATHON_NAME,
  IAccount,
  IHacker,
  ISearchParameter,
  ISponsor,
  isValidSearchParameter,
  UserType,
} from '../../config';
import * as CONSTANTS from '../../config/constants';
import { Button, ButtonVariant, H1, H2 } from '../../shared/Elements';
import { Input } from '../../shared/Form';
import ValidationErrorGenerator from '../../shared/Form/validationErrorGenerator';
import WithToasterContainer from '../../shared/HOC/withToaster';
import theme from '../../shared/Styles/theme';
import { getNestedAttr, getValueFromQuery, isSponsor } from '../../util';

import withContext from '../../shared/HOC/withContext';
import { FilterComponent } from './Filters';
import { ResultsTable } from './ResultsTable';

interface IResult {
  /**
   * For now, we aren't exposing 'selected' attribute. We set it default equal to true.
   * This is used for batch operations for changing hacker data.
   */
  selected: boolean;
  hacker: IHacker;
}

interface ISearchState {
  model: string;
  query: ISearchParameter[];
  results: IResult[];
  searchBar: string;
  loading: boolean;
  viewSaved: boolean;
  account?: IAccount;
  sponsor?: ISponsor;
}

class SearchContainer extends React.Component<{}, ISearchState> {
  constructor(props: {}) {
    super(props);
    this.state = {
      model: 'hacker',
      query: this.getSearchFromQuery(),
      results: [],
      searchBar: this.getSearchBarFromQuery(),
      loading: false,
      viewSaved: false,
    };

    this.onFilterChange = this.onFilterChange.bind(this);
    this.triggerSearch = this.triggerSearch.bind(this);
    this.downloadData = this.downloadData.bind(this);
    this.onResetForm = this.onResetForm.bind(this);
    this.onSearchBarChanged = this.onSearchBarChanged.bind(this);
  }

  public render() {
    const { searchBar, account, query, loading, viewSaved } = this.state;
    return (
      <Flex flexDirection={'column'}>
        <Helmet>
          <title> Search | {HACKATHON_NAME}</title>
        </Helmet>
        <Box width={1 / 6} alignSelf={'center'}>
          <H1 color={theme.colors.red} fontSize={'30px'}>
            Search Hackers
          </H1>
        </Box>
        <Box width={1}>
          <Flex>
            <Box width={1 / 6} mx={2}>
              <H2>Filters</H2>
              <FilterComponent
                initFilters={query}
                onChange={this.onFilterChange}
                onResetForm={this.onResetForm}
                loading={loading}
              />
            </Box>
            <Box width={5 / 6} mx={2}>
              <Flex flexDirection={'column'}>
                <Box width={6 / 6}>
                  <Flex justifyContent={'space-between'}>
                    <Box alignSelf={'flex-start'} width={0.5}>
                      <Input
                        onChange={this.onSearchBarChanged}
                        placeholder={'Refine your search...'}
                        style={{ marginTop: 5 }}
                        value={searchBar}
                      />
                    </Box>
                    <Box mr={'10px'}>
                      {account && isSponsor(account) && (
                        <Button
                          onClick={this.toggleSaved}
                          style={{ marginRight: '10px' }}
                          variant={ButtonVariant.Secondary}
                          isOutlined={true}
                        >
                          View {viewSaved ? 'All' : 'Saved'}
                        </Button>
                      )}
                      <Button
                        onClick={this.downloadData}
                        variant={ButtonVariant.Secondary}
                        isOutlined={true}
                      >
                        Export Hackers
                      </Button>
                    </Box>
                  </Flex>
                </Box>
                <ResultsTable
                  results={this.filter()}
                  loading={loading}
                  userType={account ? account.accountType : UserType.UNKNOWN}
                  filter={searchBar}
                />
              </Flex>
            </Box>
          </Flex>
        </Box>
      </Flex>
    );
  }
  public async componentDidMount() {
    const account = (await Account.getSelf()).data.data;
    this.setState({ account });

    if (isSponsor(account)) {
      const sponsor = (await Sponsor.getSelf()).data.data;
      this.setState({ sponsor });
    }
    await this.triggerSearch();
  }
  private getSearchFromQuery(): ISearchParameter[] {
    const search = getValueFromQuery('q');
    if (!search) {
      return [];
    }
    try {
      const searchParam = JSON.parse(search);
      if (!Array.isArray(searchParam)) {
        return [];
      }
      const isValidSearch =
        searchParam
          .map(
            (value: any): boolean => {
              return isValidSearchParameter(value);
            }
          )
          .indexOf(false) === -1;
      return isValidSearch ? searchParam : [];
    } catch (e) {
      return [];
    }
  }

  private getSearchBarFromQuery(): string {
    const search = getValueFromQuery('searchBar');
    return search ? decodeURIComponent(search) : '';
  }

  private downloadData(): void {
    const headers = [
      { label: CONSTANTS.FIRST_NAME_LABEL, key: 'accountId.firstName' },
      { label: CONSTANTS.LAST_NAME_LABEL, key: 'accountId.lastName' },
      { label: CONSTANTS.EMAIL_LABEL, key: 'accountId.email' },
      { label: CONSTANTS.SCHOOL_LABEL, key: 'application.general.school' },
      {
        label: CONSTANTS.FIELD_OF_STUDY_LABEL,
        key: 'application.general.fieldOfStudy',
      },
      {
        label: CONSTANTS.GRADUATION_YEAR_LABEL,
        key: 'application.general.graduationYear',
      },
      { label: CONSTANTS.DEGREE_LABEL, key: 'application.general.degree' },
      {
        label: CONSTANTS.JOBINTEREST_LABEL,
        key: 'application.general.jobInterest',
      },
    ];
    // Return all fields for admin, and only subset for sponsors
    if (
      this.state.account &&
      this.state.account.accountType === UserType.STAFF
    ) {
      headers.push({ label: 'Resume', key: 'application.general.URL.resume' });
      headers.push({ label: 'Github', key: 'application.general.URL.github' });
      headers.push({
        label: CONSTANTS.DRIBBBLE_LINK_LABEL,
        key: 'application.general.URL.dribbble',
      });
      headers.push({
        label: CONSTANTS.PERSONAL_LABEL,
        key: 'application.general.URL.personal',
      });
      headers.push({
        label: CONSTANTS.LINKEDIN_LINK_LABEL,
        key: 'application.general.URL.linkedin',
      });
      headers.push({
        label: CONSTANTS.OTHER_LINK_LABEL,
        key: 'application.general.URL.other',
      });
      headers.push({
        label: CONSTANTS.SKILLS_LABEL,
        key: 'application.shortAnswer.skills',
      });
      headers.push({
        label: CONSTANTS.COMMENTS_LABEL,
        key: 'application.shortAnswer.comments',
      });
      headers.push({
        label: CONSTANTS.QUESTION1_REQUEST_LABEL,
        key: 'application.shortAnswer.question1',
      });
      headers.push({
        label: CONSTANTS.QUESTION2_REQUEST_LABEL,
        key: 'application.shortAnswer.question2',
      });
      headers.push({
        label: CONSTANTS.SHIRT_SIZE_LABEL,
        key: 'application.accommodation.shirtSize',
      });
      headers.push({
        label: CONSTANTS.ATTENDENCE_OPTION_PREFERENCE_LABEL,
        key: 'application.accommodation.attendancePreference',
      });
      headers.push({
        label: CONSTANTS.IMPAIRMENTS_LABEL,
        key: 'application.accommodation.impairments',
      });
      headers.push({
        label: CONSTANTS.BARRIERS_LABEL,
        key: 'application.accommodation.barriers',
      });
      headers.push({
        label: CONSTANTS.TRAVEL_LABEL,
        key: 'application.accommodation.travel',
      });
      headers.push({
        label: CONSTANTS.ETHNICITY_LABEL,
        key: 'application.other.ethnicity',
      });
      headers.push({ label: CONSTANTS.GENDER_LABEL, key: 'accountId.gender' });
      headers.push({
        label: CONSTANTS.PRONOUN_LABEL,
        key: 'accountId.pronoun',
      });
    }
    const tempHeaders: string[] = [];
    headers.forEach((header) => {
      tempHeaders.push(header.label);
    });
    const csvData: string[] = [tempHeaders.join('\t')];
    this.filter().forEach((result) => {
      if (result.selected) {
        const row: string[] = [];
        headers.forEach((header) => {
          let value;
          if (header.key.indexOf('.') >= 0) {
            const nestedAttr = header.key.split('.');
            value = getNestedAttr(result.hacker, nestedAttr);
          } else {
            value = result.hacker[header.key];
          }
          row.push(value);
        });
        csvData.push(row.join('\t'));
      }
    });
    fileDownload(csvData.join('\n'), 'hackerData.tsv', 'text/tsv');
  }

  private async triggerSearch(): Promise<void> {
    this.setState({ loading: true });
    const { model, query } = this.state;
    try {
      const response = await Search.search(model, query, {
        expand: true,
      });
      const isArray = Array.isArray(response.data.data);
      const tableData = isArray
        ? response.data.data.map((v) => ({
            selected: true,
            hacker: v,
          }))
        : [];
      this.setState({ results: tableData, loading: false });
    } catch (e) {
      ValidationErrorGenerator(e.data);
      this.setState({ loading: false });
    }
  }
  private onResetForm() {
    this.setState({ query: [] });
    this.updateQueryURL([], this.state.searchBar);
  }

  private onFilterChange(newFilters: ISearchParameter[]) {
    this.setState({
      query: newFilters,
    });
    this.updateQueryURL(newFilters, this.state.searchBar);
    this.triggerSearch();
  }

  private onSearchBarChanged(e: any) {
    const searchBar = e.target.value;
    this.setState({ searchBar });
    this.updateQueryURL(this.state.query, searchBar);
  }

  private updateQueryURL(filters: ISearchParameter[], searchBar: string) {
    const newSearch = `?q=${encodeURIComponent(
      JSON.stringify(filters)
    )}&searchBar=${encodeURIComponent(searchBar)}`;
    window.history.replaceState(
      null,
      '',
      window.location.href.split('?')[0] + newSearch
    );
  }

  private filter() {
    const { sponsor, viewSaved, results } = this.state;
    const searchBar = this.state.searchBar.toLowerCase();
    return results.filter(({ hacker }) => {
      const { account } = hacker;
      let foundAcct;
      if (typeof account !== 'number') {
        if (account) {
          const fullName = `${account.firstName} ${
            account.lastName
          }`.toLowerCase();
          foundAcct =
            fullName.includes(searchBar) ||
            account.email.toLowerCase().includes(searchBar) ||
            account.phoneNumber.toString().includes(searchBar) ||
            account.gender.toLowerCase().includes(searchBar) ||
            (account.identifier &&
              account.identifier === Number.parseInt(searchBar));
        }
      } else {
        foundAcct = account.toString().includes(searchBar);
      }
      const foundHacker =
        hacker.identifier.toString().includes(searchBar) ||
        hacker.application.general.school.toLowerCase().includes(searchBar) ||
        hacker.application.general.degree.toLowerCase().includes(searchBar) ||
        hacker.application.general.fieldOfStudy
          .map((field) => field.toLowerCase())
          .includes(searchBar) ||
        hacker.application.general.graduationYear
          .toString()
          .includes(searchBar) ||
        hacker.application.general.jobInterest
          .toLowerCase()
          .includes(searchBar) ||
        hacker.status.toLowerCase().includes(searchBar) ||
        hacker.application.shortAnswer.question1
          .toLowerCase()
          .includes(searchBar) ||
        hacker.application.shortAnswer.question2
          .toLowerCase()
          .includes(searchBar) ||
        hacker.application.accommodation.shirtSize
          .toLowerCase()
          .includes(searchBar) ||
        hacker.application.accommodation.attendancePreference
          .toLowerCase()
          .includes(searchBar) ||
        (hacker.application.shortAnswer.skills &&
          hacker.application.shortAnswer.skills
            .toString()
            .toLowerCase()
            .includes(searchBar));

      const isSavedBySponsorIfToggled =
        !viewSaved ||
        (sponsor && sponsor.nominees.some((n) => n === hacker.id));

      return (foundAcct || foundHacker) && isSavedBySponsorIfToggled;
    });
  }

  private toggleSaved = async () => {
    // Resets the sponsor if they made changes to their saved hackers
    const sponsor = (await Sponsor.getSelf()).data.data;
    const { viewSaved } = this.state;
    if (sponsor) {
      this.setState({ sponsor, viewSaved: !viewSaved });
    }
  };
}

export default withContext(WithToasterContainer(SearchContainer));
